<template>
  <div class="print-invoice-container" :style="containerStyle">
    <!-- Non-printable Controls -->
    <div class="no-print control-bar">
      <div class="control-group">
        <span class="control-label">Cỡ chữ:</span>
        <el-input-number v-model="fontSize" :min="7" :max="40" size="small" style="width: 100px;"></el-input-number>
        <span style="margin-left: 5px;">px</span>
      </div>
      <div class="control-group">
        <span class="control-label">Khổ giấy:</span>
        <el-select v-model="paperSize" size="small" style="width: 80px;">
          <el-option label="A4" value="A4"></el-option>
          <el-option label="A5" value="A5"></el-option>
        </el-select>
      </div>
      <div class="control-group">
        <span class="control-label">Hướng in:</span>
        <el-select v-model="orientation" size="small" style="width: 110px;">
          <el-option label="Khổ dọc" value="portrait"></el-option>
          <el-option label="Khổ ngang" value="landscape"></el-option>
        </el-select>
      </div>
      <div class="control-actions">
        <el-tooltip effect="dark" content="Cấu hình in" placement="bottom">
          <el-button type="warning" size="medium" circle icon="el-icon-setting" @click="showConfigDialog = true"></el-button>
        </el-tooltip>
        <el-tooltip effect="dark" content="Xuất Excel" placement="bottom">
          <el-button type="success" size="medium" circle icon="el-icon-download" @click="exportExcel"></el-button>
        </el-tooltip>
        <el-tooltip effect="dark" content="In phiếu" placement="bottom">
          <el-button type="primary" size="medium" circle icon="el-icon-printer" @click="printReceipt"></el-button>
        </el-tooltip>
        <el-tooltip effect="dark" content="Đóng" placement="bottom">
          <el-button type="danger" size="medium" circle icon="el-icon-close" @click="closeTab"></el-button>
        </el-tooltip>
      </div>
    </div>

    <!-- Dialog Cấu hình in -->
    <el-dialog 
      title="Cấu hình in phiếu thu" 
      :visible.sync="showConfigDialog" 
      width="720px" 
      custom-class="sk-dialog sk-dialog--md" 
      append-to-body
    >
      <div class="sk-dialog__content" style="display: flex; gap: 16px; align-items: stretch;">
        <!-- Section 1: Tùy chọn in -->
        <div class="sk-panel" style="flex: 1; margin: 0;">
          <div class="sk-panel-head">
            <div class="sk-panel-icon"><i class="el-icon-setting" /></div>
            <div>
              <p class="sk-panel-title">Tùy chọn in</p>
              <p class="sk-panel-subtitle">Hiển thị các khoản thu và thời gian</p>
            </div>
          </div>
          
          <div class="sk-panel__body" style="padding: 16px 0 0;">
            <el-form label-width="210px" label-position="left">
              <el-form-item label="Khoản có đơn giá:">
                <el-switch v-model="printConfig.printWithPrice"></el-switch>
              </el-form-item>
              <el-form-item label="Khoản chưa có đơn giá:">
                <el-switch v-model="printConfig.printWithoutPrice"></el-switch>
              </el-form-item>
              <el-form-item label="Khoản tổng tiền = 0:">
                <el-switch v-model="printConfig.printZeroAmount"></el-switch>
              </el-form-item>
              <el-form-item label="Khoản số lượng = 0:">
                <el-switch v-model="printConfig.printZeroQuantity"></el-switch>
              </el-form-item>
              <el-form-item label="Hiển thị thời gian in:">
                <el-switch v-model="printConfig.showTime"></el-switch>
              </el-form-item>
            </el-form>
          </div>
        </div>

        <!-- Section 2: Hiển thị cột -->
        <div class="sk-panel" style="flex: 1; margin: 0;">
          <div class="sk-panel-head">
            <div class="sk-panel-icon sk-panel-icon--success"><i class="el-icon-document" /></div>
            <div>
              <p class="sk-panel-title">Hiển thị cột</p>
              <p class="sk-panel-subtitle">Ẩn/hiển thị cột dữ liệu trên bảng</p>
            </div>
          </div>
          
          <div class="sk-panel__body" style="padding: 16px 0 0;">
            <el-form label-width="210px" label-position="left">
              <el-form-item label="Hiển thị cột ĐVT:">
                <el-switch v-model="printConfig.showColUnit"></el-switch>
              </el-form-item>
              <el-form-item label="Hiển thị cột Số lượng:">
                <el-switch v-model="printConfig.showColQuantity"></el-switch>
              </el-form-item>
              <el-form-item label="Hiển thị cột Đơn giá:">
                <el-switch v-model="printConfig.showColPrice"></el-switch>
              </el-form-item>
              <el-form-item label="Hiển thị cột Thuế:">
                <el-switch v-model="printConfig.showColTax"></el-switch>
              </el-form-item>
              <el-form-item label="Hiển thị cột Giảm giá:">
                <el-switch v-model="printConfig.showColDiscount"></el-switch>
              </el-form-item>
              <el-form-item label="Hiển thị cột Ghi chú:">
                <el-switch v-model="printConfig.showColNote"></el-switch>
              </el-form-item>
            </el-form>
          </div>
        </div>
      </div>
      <div slot="footer" class="sk-dialog__footer">
        <el-button class="btn-cancel" plain @click="showConfigDialog = false">Hủy</el-button>
        <el-button class="btn-save" type="primary" @click="showConfigDialog = false">Xác nhận</el-button>
      </div>
    </el-dialog>

    <!-- Printable Area (Loop for batch print) -->
    <div class="print-area" :class="[paperSize.toLowerCase(), orientation]">
      <div 
        class="print-page" 
        :class="{ 'two-copies': copies === 2 }"
        v-for="(invoiceInfo, invoiceIndex) in filteredInvoices" 
        :key="invoiceIndex"
      >
        <div 
          class="invoice-page" 
          v-for="copyIndex in copies" 
          :key="`${invoiceIndex}-${copyIndex}`"
        >
          <div class="invoice-header">
            <div class="logo-box">
              <img v-if="schoolLogo" :src="schoolLogo" alt="Logo" />
              <div v-else class="logo-placeholder">
                <span>Logo Trường</span>
              </div>
            </div>
            <div class="header-info">
              <h2 class="company-name">{{ companyName }}</h2>
              <p class="company-address">{{ companyAddress }}</p>
              <h1 class="invoice-title">PHIẾU THU HỌC PHÍ</h1>
              <p class="invoice-subtitle">Tháng {{ invoiceInfo.month || '...' }}/{{ invoiceInfo.year || '...' }} - Đợt {{ invoiceInfo.turn || '...' }}</p>
            </div>
            <div class="qr-box">
              <img v-if="invoiceInfo.urlOnlinePayment" :src="invoiceInfo.urlOnlinePayment" alt="QR Code" />
              <div v-else class="logo-placeholder">
                <span>Mã QR</span>
              </div>
            </div>
          </div>

          <div class="invoice-meta">
            <div class="meta-left">
              <p><strong>Họ tên:</strong> <span class="meta-value">{{ invoiceInfo.fullName }} ({{ invoiceInfo.studentCode }})</span></p>
              <p><strong>Lớp:</strong> <span class="meta-value">{{ invoiceInfo.className || '' }}</span></p>
              <p><strong>SĐT:</strong> <span class="meta-value">{{ invoiceInfo.parentPhone || '' }}</span></p>
            </div>
            <div class="meta-right">
              <p><strong>Mã:</strong> <span class="meta-dots">..................</span></p>
              <p><strong>Số:</strong> <span class="meta-dots">..................</span></p>
              <p><strong>Quyển:</strong> <span class="meta-dots">..................</span></p>
            </div>
          </div>

          <table class="invoice-table">
            <thead>
              <tr>
                <th width="5%" style="text-align: center;">STT</th>
                <th width="23%" style="text-align: center;">Tên khoản thu</th>
                <th v-if="printConfig.showColUnit" width="7%" style="text-align: center;">ĐVT</th>
                <th v-if="printConfig.showColQuantity" width="5%" style="text-align: center;">SL</th>
                <th v-if="printConfig.showColPrice" width="12%" style="text-align: center;">Đơn giá</th>
                <th v-if="printConfig.showColTax" width="10%" style="text-align: center;">Thuế</th>
                <th v-if="printConfig.showColDiscount" width="10%" style="text-align: center;">Giảm giá</th>
                <th width="13%" style="text-align: center;">Thành tiền</th>
                <th v-if="printConfig.showColNote" width="25%" style="text-align: center;">Ghi chú</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in invoiceInfo.invoiceDetails" :key="index" :class="{ 'child-row': item._isChild }">
                <td style="text-align: center;">{{ item._isChild ? '' : (item._displayIndex || (index + 1)) }}</td>
                <td style="text-align: left;" :style="item._isChild ? { 'padding-left': `${(item._depth || 1) * 12}px` } : {}">
                  <span v-if="item._isChild" class="child-indicator">- </span>{{ item.revenueName }}
                </td>
                <td v-if="printConfig.showColUnit" style="text-align: center;">{{ item.unit || '' }}</td>
                <td v-if="printConfig.showColQuantity" style="text-align: center;">{{ formatQuantity(item.quantity) }}</td>
                <td v-if="printConfig.showColPrice" style="text-align: right;">{{ item.price | formatCurrency }}</td>
                <td v-if="printConfig.showColTax" style="text-align: center;">{{ item._isChild ? '' : formatTax(item.taxPercentage) }}</td>
                <td v-if="printConfig.showColDiscount" style="text-align: right;">
                  <span v-if="item.discountNumber">{{ item.discountNumber | formatCurrency }}</span>
                </td>
                <td style="text-align: right; font-weight: 500;">{{ item.amount | formatCurrency }}</td>
                <td v-if="printConfig.showColNote" style="text-align: left;">{{ item.description || '' }}</td>
              </tr>
            </tbody>
          </table>
          
          <table class="summary-table">
            <tbody>
              <tr>
                <td class="summary-label">Tổng cộng</td>
                <td class="summary-value highlight-total">{{ calcTotal(invoiceInfo) | formatCurrency }}</td>
              </tr>
              <tr v-if="invoiceInfo.beforeMoney >= 0">
                <td class="summary-label">Tiền thừa phiếu thu trước</td>
                <td class="summary-value">{{ (invoiceInfo.beforeMoney || 0) | formatCurrency }}</td>
              </tr>
              <tr v-else>
                <td class="summary-label">Tiền thiếu phiếu thu trước</td>
                <td class="summary-value">{{ (invoiceInfo.beforeMoney || 0) | formatCurrency }}</td>
              </tr>
              <tr>
                <td class="summary-label">Số tiền đã trả</td>
                <td class="summary-value">{{ (invoiceInfo.collectedMoney || 0) | formatCurrency }}</td>
              </tr>
              <tr>
                <td class="summary-label">{{ balanceLabel(invoiceInfo.afterMoney) }}</td>
                <td class="summary-value" :class="{ 'highlight-debt': Number(invoiceInfo.afterMoney || 0) < 0, 'highlight-surplus': Number(invoiceInfo.afterMoney || 0) > 0 }">
                  {{ Math.abs(Number(invoiceInfo.afterMoney || 0)) | formatCurrency }}
                </td>
              </tr>
              <tr>
                <td colspan="2" class="words-amount">
                  Bằng chữ: <strong>{{ calcWords(invoiceInfo.afterMoney || 0) }}</strong>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="footer-section" style="page-break-inside: avoid; break-inside: avoid;">
            <div class="invoice-notes">
              <p><strong>Ghi chú:</strong> PHHS vui lòng thanh toán tại văn phòng trường hoặc qua tài khoản ngân hàng của nhà trường trước ngày mùng 10 hàng tháng! Trân trọng cảm ơn.</p>
              <div class="bank-info">
                <p><strong>STK:</strong> <span class="bank-value">{{ invoiceInfo.bankAccount || '......................' }}</span></p>
                <p><strong>Chủ TK:</strong> <span class="bank-value">{{ invoiceInfo.bankOwner || '......................' }}</span></p>
                <p><strong>Ngân hàng:</strong> <span class="bank-value">{{ invoiceInfo.bankName || '......................' }}</span></p>
              </div>
            </div>

            <div class="invoice-footer">
              <div class="footer-left">
                <p class="signature-title">Người nộp tiền</p>
                <p class="signature-sub">(Ký, họ tên)</p>
              </div>
              <div class="footer-right">
                <p class="signature-time">{{ formattedPrintDate }}</p>
                <p class="signature-title">Người lập phiếu</p>
                <p class="signature-sub">(Ký, họ tên)</p>
                <div class="signature-name">
                  <strong>{{ invoiceInfo.userPrint || 'Trần Huy Hoàng' }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { exportTuitionInvoiceToExcel } from "@/commonFuncion/ExportTuitionInvoiceExcel";
import { mapGetters } from "vuex";
import { formatQuantity as formatQuantityValue } from "@/components/tutition_v3/utils/quantity";

const ChuSo = [" không ", " một ", " hai ", " ba ", " bốn ", " năm ", " sáu ", " bảy ", " tám ", " chín "];
const Tien = ["", " nghìn", " triệu", " tỷ", " nghìn tỷ", " triệu tỷ"];

function DocSo3ChuSo(baso) {
  let tram, chuc, donvi, KetQua = "";
  tram = parseInt(baso / 100);
  chuc = parseInt((baso % 100) / 10);
  donvi = baso % 10;
  if (tram == 0 && chuc == 0 && donvi == 0) return "";
  if (tram != 0) {
    KetQua += ChuSo[tram] + " trăm ";
    if ((chuc == 0) && (donvi != 0)) KetQua += " linh ";
  }
  if ((chuc != 0) && (chuc != 1)) {
    KetQua += ChuSo[chuc] + " mươi";
    if ((chuc == 0) && (donvi != 0)) KetQua += " linh ";
  }
  if (chuc == 1) KetQua += " mười ";
  switch (donvi) {
    case 1:
      if ((chuc != 0) && (chuc != 1)) KetQua += " mốt ";
      else KetQua += ChuSo[donvi];
      break;
    case 5:
      if (chuc == 0) KetQua += ChuSo[donvi];
      else KetQua += " lăm ";
      break;
    default:
      if (donvi != 0) KetQua += ChuSo[donvi];
      break;
  }
  return KetQua;
}

function DocTienBangChu(SoTien) {
  let lan = 0, i = 0, so = 0, KetQua = "", tmp = "", ViTri = [];
  if (SoTien < 0) return "Số tiền âm!";
  if (SoTien == 0) return "Không đồng";
  so = SoTien > 0 ? SoTien : -SoTien;
  if (SoTien > 8999999999999999) return "Số quá lớn!";
  
  ViTri[5] = Math.floor(so / 1000000000000000); so -= ViTri[5] * 1000000000000000;
  ViTri[4] = Math.floor(so / 1000000000000);    so -= ViTri[4] * 1000000000000;
  ViTri[3] = Math.floor(so / 1000000000);       so -= ViTri[3] * 1000000000;
  ViTri[2] = parseInt(so / 1000000);
  ViTri[1] = parseInt((so % 1000000) / 1000);
  ViTri[0] = parseInt(so % 1000);
  
  if (ViTri[5] > 0) lan = 5;
  else if (ViTri[4] > 0) lan = 4;
  else if (ViTri[3] > 0) lan = 3;
  else if (ViTri[2] > 0) lan = 2;
  else if (ViTri[1] > 0) lan = 1;
  else lan = 0;
  
  for (i = lan; i >= 0; i--) {
    tmp = DocSo3ChuSo(ViTri[i]);
    KetQua += tmp;
    if (ViTri[i] > 0) KetQua += Tien[i];
    if ((i > 0) && (tmp.length > 0)) KetQua += ',';
  }
  if (KetQua.slice(-1) == ',') KetQua = KetQua.slice(0, -1);
  KetQua = KetQua.trim();
  KetQua = KetQua.charAt(0).toUpperCase() + KetQua.slice(1);
  return KetQua + " đồng";
}

export default {
  name: "BatchPrintInvoicePreview",
  data() {
    return {
      fontSize: 18,
      paperSize: "A4",
      orientation: "portrait",
      
      savedCompanyName: "",
      className: "",
      parentPhone: "",
      bankAccount: "",
      bankOwner: "",
      bankName: "",
      
      copies: 1,
      invoices: [],
      showConfigDialog: false,
      printConfig: {
        printWithPrice: true,
        printWithoutPrice: true,
        printZeroAmount: false, // Default to false (không/0)
        printZeroQuantity: false, // Default to false (không)
        showTime: false, // Default to false (không)
        showColUnit: true,
        showColQuantity: true,
        showColPrice: true,
        showColTax: true,
        showColDiscount: true,
        showColNote: true
      }
    };
  },
  computed: {
    ...mapGetters("schoolStore", ["schoolData", "schoolInfo"]),
    companyName() {
      return this.schoolData?.schoolName || this.savedCompanyName || "";
    },
    companyAddress() {
      return this.schoolData?.schoolAddress || "";
    },
    schoolLogo() {
      return this.schoolData?.schoolAvatar || "";
    },
    containerStyle() {
      return {
        '--print-font-size': `${this.fontSize}px`
      };
    },
    filteredInvoices() {
      return this.invoices.map(inv => {
        let filteredDetails = (inv.invoiceDetails || []).filter(item => {
          // In các khoản có đơn giá
          const hasPrice = Number(item.price || 0) > 0;
          if (hasPrice && !this.printConfig.printWithPrice) return false;

          // In các khoản chưa có đơn giá
          const noPrice = !item.price || Number(item.price) === 0;
          if (noPrice && !this.printConfig.printWithoutPrice) return false;

          // In các khoản tổng tiền bằng 0
          const zeroAmount = Number(item.amount || 0) === 0;
          if (zeroAmount && !this.printConfig.printZeroAmount) return false;

          // In các khoản số lượng bằng 0
          const zeroQuantity = Number(item.quantity || 0) === 0;
          if (zeroQuantity && !this.printConfig.printZeroQuantity) return false;

          return true;
        });

        // Recalculate display index
        let displayIndex = 1;
        let mappedDetails = filteredDetails.map(item => {
          if (!item._isChild) {
            return { ...item, _displayIndex: displayIndex++ };
          }
          return item;
        });

        return {
          ...inv,
          invoiceDetails: mappedDetails
        };
      });
    },
    formattedPrintDate() {
      const h = new Date().getHours().toString().padStart(2, "0");
      const m = new Date().getMinutes().toString().padStart(2, "0");
      const dateStr = `Ngày ${this.currentDate()} tháng ${this.currentMonth()} năm ${this.currentYear()}`;
      if (this.printConfig.showTime) {
        return `${h}:${m}, ${dateStr}`;
      }
      return dateStr;
    }
  },
  mounted() {
    this.loadData();
    this.updatePrintStyle();
    this.$store.dispatch('schoolStore/fetchDataSchoolData');
  },
  watch: {
    paperSize() { this.updatePrintStyle(); },
    orientation() { this.updatePrintStyle(); }
  },
  methods: {
    formatQuantity(value) {
      return formatQuantityValue(value, false, "");
    },

    formatTax(val) {
      if (val === null || val === undefined || val === '') return '';
      const tax = Number(val);
      if (tax === -2) return 'Không chịu thuế';
      if (tax === -1) return 'Không kê khai tính/nộp thuế';
      return `${tax}%`;
    },
    currentDate() {
      return new Date().getDate().toString().padStart(2, "0");
    },
    currentMonth() {
      return (new Date().getMonth() + 1).toString().padStart(2, "0");
    },
    currentYear() {
      return new Date().getFullYear();
    },
    printTimeStr() {
      const d = new Date();
      const h = d.getHours().toString().padStart(2, "0");
      const m = d.getMinutes().toString().padStart(2, "0");
      return `${h}:${m}, ngày ${this.currentDate()} tháng ${this.currentMonth()} năm ${this.currentYear()}`;
    },
    updatePrintStyle() {
      let styleEl = document.getElementById('dynamic-print-style');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'dynamic-print-style';
        document.head.appendChild(styleEl);
      }
      styleEl.innerHTML = `@page { size: ${this.paperSize} ${this.orientation}; margin: 0; }`;
    },
    loadData() {
      try {
        const storedData = localStorage.getItem("tuition_batch_print_data");
        if (storedData) {
          const parsed = JSON.parse(storedData);
          
          let rawInvoices = parsed.invoices || [];
          this.invoices = rawInvoices.map(inv => {
            let flatDetails = [];
            let displayIndex = 1;

            const appendChildren = (children, depth = 1) => {
              children.forEach(child => {
                flatDetails.push({ ...child, _isChild: true, _depth: depth });
                if (Array.isArray(child.invoiceDetailItems) && child.invoiceDetailItems.length > 0) {
                  appendChildren(child.invoiceDetailItems, depth + 1);
                }
              });
            };

            (inv.invoiceDetails || []).forEach(parent => {
              flatDetails.push({ ...parent, _displayIndex: displayIndex++, _isChild: false, _depth: 0 });
              if (Array.isArray(parent.invoiceDetailItems) && parent.invoiceDetailItems.length > 0) {
                appendChildren(parent.invoiceDetailItems);
              }
            });
            return { ...inv, invoiceDetails: flatDetails };
          });
          
          this.copies = Number(parsed.copies) || 1;
          
          if (this.copies === 2) {
            this.paperSize = "A4";
            this.orientation = "landscape";
            this.fontSize = 12;
          }
          
          if (parsed.companyName) this.savedCompanyName = parsed.companyName;
          if (parsed.appName) this.appName = parsed.appName;
        } else {
          this.$message.warning("Không tìm thấy dữ liệu phiếu thu để in hàng loạt.");
        }
      } catch (err) {
        console.error("Lỗi parse data in hàng loạt:", err);
      }
    },
    calcTotal(invoice) {
      if (!invoice.invoiceDetails) return 0;
      return invoice.invoiceDetails
        .filter(item => !item._isChild)
        .reduce((sum, item) => sum + Number(item.amount || 0), 0);
    },
    balanceLabel(amount) {
      const balance = Number(amount || 0);
      if (balance > 0) return "Số tiền còn thừa";
      if (balance < 0) return "Số tiền còn thiếu";
      return "Số tiền còn lại";
    },
    calcWords(money) {
      return DocTienBangChu(Math.abs(Number(money || 0)));
    },
    printReceipt() {
      window.print();
    },
    async exportExcel() {
      try {
        const loading = this.$loading({
          lock: true,
          text: 'Đang tạo file Excel...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        });
        
        await exportTuitionInvoiceToExcel({
          invoices: this.filteredInvoices,
          companyName: this.companyName,
          companyAddress: this.companyAddress,
          bankAccount: this.bankAccount,
          bankOwner: this.bankOwner,
          bankName: this.bankName,
          schoolLogo: this.schoolLogo,
          showTime: this.printConfig.showTime,
          calcWords: this.calcWords || ((money) => DocTienBangChu(Math.abs(Number(money || 0))))
        });
        
        loading.close();
        this.$message.success("Xuất file Excel thành công!");
      } catch (error) {
        console.error("Lỗi xuất Excel:", error);
        this.$message.error("Có lỗi xảy ra khi xuất file Excel.");
      }
    },
    closeTab() {
      window.close();
    }
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog.scss";

.print-invoice-container {
  min-height: 100vh;
  background-color: #e5e7eb;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Control Bar */
.control-bar {
  background: #ffffff;
  padding: 12px 24px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
  margin-bottom: 24px;
  width: max-content;
  max-width: 95%;
  margin-left: auto;
  margin-right: auto;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  position: sticky;
  top: 15px;
  z-index: 100;
  border: 1px solid #f3f4f6;
  
  .control-group {
    display: flex;
    align-items: center;
    background: #f9fafb;
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
  }
  
  .control-label {
    font-size: 13px;
    font-weight: 600;
    margin-right: 10px;
    color: #4b5563;
    white-space: nowrap;
  }
  
  .control-actions {
    margin-left: auto;
    display: flex;
    gap: 10px;
  }
}

/* Print Area */
.print-area {
  background: transparent;
  margin: 0 auto;
  color: #111827;
  font-size: var(--print-font-size, 18px);
  line-height: 1.5;
  box-sizing: border-box;
  
  &.a4 {
    &.portrait { width: 210mm; }
    &.landscape { width: 297mm; }
  }
  
  &.a5 {
    &.portrait { width: 148mm; }
    &.landscape { width: 210mm; }
  }
}

.print-page {
  page-break-after: always;
  background: transparent;
  box-sizing: border-box;
}

.print-page.two-copies {
  display: flex;
  flex-direction: row;
  gap: 15mm;
  align-items: stretch;

  .logo-box, .qr-box {
    width: 100px !important;
    height: 100px !important;
  }
}

.invoice-page {
  background: #ffffff;
  padding: 20px 30px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  box-sizing: border-box;
  flex: 1;
}

/* Header */
.invoice-header {
  display: flex;
  margin-bottom: 20px;
  align-items: center;
  border-bottom: 1px dashed #d1d5db;
  padding-bottom: 15px;
  
  .logo-box, .qr-box {
    width: 150px;
    height: 150px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    
    .logo-placeholder {
      color: #6b7280;
      font-size: 12px;
      text-align: center;
      font-weight: 500;
    }
  }
  
  .header-info {
    flex: 1;
    text-align: center;
    padding: 0 15px;
    
    .company-name {
      margin: 0 0 4px 0;
      font-size: 1.25em;
      font-weight: 700;
      color: #1f2937;
    }
    .company-address {
      margin: 0 0 12px 0;
      font-size: 0.9em;
      color: #4b5563;
    }
    .invoice-title {
      margin: 0;
      font-size: 1.3em;
      font-weight: bold;
      text-transform: uppercase;
      color: #111827;
      letter-spacing: 0.5px;
    }
    .invoice-subtitle {
      margin: 4px 0 0;
      font-size: 1em;
      font-style: italic;
      color: #374151;
    }
  }
}

.invoice-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  
  p { margin: 4px 0; }
  .meta-value { font-weight: 600; color: #1f2937; }
  .meta-dots { color: #9ca3af; }
  
  .meta-right {
    text-align: right;
  }
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;
  font-size: 0.95em;
  
  th, td {
    border: 1px solid #9ca3af;
    padding: 8px 10px;
  }
  
  th {
    font-weight: 600;
    background-color: #f3f4f6;
    color: #1f2937;
    -webkit-print-color-adjust: exact;
  }
}

.invoice-table {
  margin-bottom: 0;
  border-bottom: none;
  
  tbody tr:nth-child(even) {
    background-color: #f9fafb;
    -webkit-print-color-adjust: exact;
  }
  
  .child-row {
    color: #4b5563;
    font-style: italic;
    background-color: #ffffff !important;
  }
  .child-indicator {
    color: #9ca3af;
    font-style: normal;
  }
}

.summary-table {
  margin-bottom: 20px;
  border-top: none;
  
  td {
    padding: 8px 10px;
  }
  
  .summary-label {
    text-align: right;
    font-weight: 600;
    width: 75%;
    color: #374151;
  }
  
  .summary-value {
    text-align: right;
    width: 25%;
    font-weight: 500;
  }
  
  .highlight-total {
    font-weight: 700;
    font-size: 1.1em;
    color: #111827;
  }
  
  .highlight-debt {
    color: #dc2626;
    font-weight: 700;
  }

  .highlight-surplus {
    color: #15803d;
    font-weight: 700;
  }
  
  .words-amount {
    font-style: italic;
    text-align: right;
    font-size: 1em;
    background-color: #f3f4f6;
    -webkit-print-color-adjust: exact;
  }
}

.invoice-notes {
  margin-bottom: 25px;
  font-size: 0.9em;
  color: #4b5563;
  
  p { margin: 6px 0; }
  
  .bank-info {
    display: flex;
    justify-content: space-between;
    background-color: #f9fafb;
    padding: 10px 15px;
    border: 1px dashed #d1d5db;
    border-radius: 4px;
    margin-top: 10px;
    
    p { margin: 0; }
    .bank-value { font-weight: 600; color: #1f2937; }
  }
}

.invoice-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  
  .footer-left, .footer-right {
    text-align: center;
    width: 45%;
  }
  
  .signature-time {
    font-style: italic;
    margin: 0 0 5px;
    color: #4b5563;
    font-size: 0.95em;
  }
  
  .signature-title {
    font-weight: bold;
    margin: 0;
    font-size: 1em;
  }
  
  .signature-sub {
    font-style: italic;
    font-size: 0.85em;
    color: #6b7280;
    margin: 4px 0 0;
  }
  
  .signature-name {
    margin-top: 70px;
    font-size: 1em;
  }
}

@media print {
  .no-print {
    display: none !important;
  }
  
  .print-invoice-container {
    background: transparent;
    padding: 0;
  }
  
  .print-area {
    width: 100% !important;
  }

  .invoice-page {
    box-shadow: none;
    margin: 0;
    padding: 10px;
    min-height: auto;
  }

  .print-page {
    padding: 10mm;
    min-height: auto;
    page-break-after: always;
    page-break-inside: avoid;
  }

  /* Remove page break after the very last element */
  .print-page:last-child {
    page-break-after: auto;
  }
}
</style>

<!-- Global (unscoped) print styles — these selectors target elements outside
     this component's scope (html, body, #app, layout wrappers). Scoped styles
     would add data-v-xxx attributes that prevent them from matching. -->
<style lang="scss">
@media print {
  html, body {
    width: auto !important;
    min-width: 0 !important;
    max-width: 100% !important;
    height: auto !important;
    min-height: auto !important;
    overflow: visible !important;
  }
  #app,
  .app-wrapper,
  .main-container,
  .app-main {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    height: auto !important;
    min-height: auto !important;
    overflow: visible !important;
    padding: 0 !important;
    margin: 0 !important;
  }
  /* Hide sidebar & navbar when printing */
  .sidebar-container,
  .fixed-header,
  .navbar {
    display: none !important;
  }
}
</style>

<!-- Global (unscoped) print styles — these selectors target elements outside
     this component's scope (html, body, #app, layout wrappers). Scoped styles
     would add data-v-xxx attributes that prevent them from matching. -->
<style lang="scss">
@media print {
  html, body {
    width: auto !important;
    min-width: 0 !important;
    max-width: 100% !important;
    height: auto !important;
    min-height: auto !important;
    overflow: visible !important;
  }
  #app,
  .app-wrapper,
  .main-container,
  .app-main {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    height: auto !important;
    min-height: auto !important;
    overflow: visible !important;
    padding: 0 !important;
    margin: 0 !important;
  }
  /* Hide sidebar & navbar when printing */
  .sidebar-container,
  .fixed-header,
  .navbar {
    display: none !important;
  }
}
</style>
